import React, { useState, useEffect, useCallback } from 'react';
import { MaterialReactTable } from 'material-react-table';
import { Box, Typography, Button } from '@mui/material';
import '../css/TrackerPage.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp, faSpinner, faEye, faUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { useParams } from 'react-router-dom';
import BACKEND_BASE_URL from './../config';

function TrackerPage() {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState({});
    const [issueDesc, setIssueDesc] = useState({});
    const [loading, setLoading] = useState(false);
    const [noRecordFound, setNoRecordFound] = useState(false);
    const [sprintLoading, setSprintLoading] = useState(false);
    const [expandedItems, setExpandedItems] = useState({});
    const [sprintData, setSprintData] = useState(null);
    const { key } = useParams();
    const [hasHandledURLKey, setHasHandledURLKey] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [setTableFilters] = useState([]);
    const [setSorting] = useState([]);

    const handleSearch = useCallback(async (customQuery) => {
        const finalQuery = customQuery || query;
        setQuery(finalQuery);
        setExpandedItems({});
        setResults({});
        setIssueDesc({});
        setLoading(true);
        try {
            const sprintData = localStorage.getItem('sprintData');
            const sprintDataParsed = sprintData ? JSON.parse(sprintData) : {};
            const sprint_id = typeof sprintDataParsed.sprint_id !== 'undefined' ? sprintDataParsed.sprint_id : ''
            const response = await fetch(`${BACKEND_BASE_URL}/issue/timeTracker/${finalQuery}?sprintId=${sprint_id}`, {
                headers: {
                    'ngrok-skip-browser-warning': 'true',
                },
            });
            const data = await response.json();
            if (data.success && data.data && data.data.history) {
                setIssueDesc({
                    summary: data.data.summary,
                    issueType: data.data.issueType,
                    assignee: data.data.assignee,
                    reporter: data.data.reporter,
                    status: data.data.status,
                    issueKey: data.data.issueKey,
                    issueUrl: data.data.issueUrl,
                });
                if(Object.entries(data.data.history).length){
                    setNoRecordFound(false);
                    setResults(data.data.history);
                } else {
                    setNoRecordFound(true);
                }
            } else {
                setNoRecordFound(true);
                setResults({});
                setIssueDesc({});
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            setResults({});
            setIssueDesc({});
        } finally {
            setLoading(false);
        }
    }, [query]);

    useEffect(() => {
        const fetchSprintData = async () => {
            setSprintLoading(true);
            try {
                const sprintData = localStorage.getItem('sprintData');
                const sprintDataParsed = sprintData ? JSON.parse(sprintData) : {};
                const sprint_id = typeof sprintDataParsed.sprint_id !== 'undefined' ? sprintDataParsed.sprint_id : '';
                const response = await fetch(`${BACKEND_BASE_URL}/sprint/activeSprint?sprintId=${sprint_id}`,
                    {
                        headers:
                        {
                            'ngrok-skip-browser-warning': 'true'
                        }
                    });
                const data = await response.json();
                if (data.success) {
                    setSprintLoading(false);
                    setSprintData(data.data);
                }
            } catch (error) {
                console.error('Error fetching sprint data:', error);
            }
        };
        fetchSprintData();
    }, []);

    useEffect(() => {
        if (key && !hasHandledURLKey) {
            handleSearch(key);
            setHasHandledURLKey(true);
        }
    }, [key, handleSearch, hasHandledURLKey]);

    const toggleExpand = (id) => {
        setExpandedItems(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const resetSearch = async () => {
        setQuery('');
        setExpandedItems({});
        setResults({});
        setIssueDesc({});
    };

    const columns = [
        {
            header: 'Type',
            accessorKey: 'issueType',
            size: 20,
        },
        {
            header: 'Key',
            accessorKey: 'key',
            size: 20,
        },
        {
            header: 'Summary',
            accessorKey: 'title',
            size: 50,
            maxSize: 50,
            Cell: ({ cell }) => (
                <div style={{ width: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {cell.getValue()}
                </div>
            ),
        },
        {
            header: 'Status',
            accessorKey: 'status',
            size: 50,
        },
        {
            header: 'Assignee',
            accessorKey: 'assignee',
            size: 80,
            Cell: ({ cell }) => cell.getValue() || 'Unassigned',
        },
        {
            header: 'Reporter',
            accessorKey: 'reporter',
            size: 80,
            Cell: ({ cell }) => cell.getValue(),
        },
        {
            header: 'View',
            accessorKey: 'view',
            enableSorting: false,
            enableColumnFilter: false,
            size: 20,
            Cell: ({ row }) => (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                        setQuery(row.original.key);
                        handleSearch(row.original.key);
                    }}
                    title="View Issue"
                >
                    <FontAwesomeIcon icon={faEye} />
                </Button>
            ),
        },
    ];


    return (
        <div className="container py-4">
            <div className="d-flex align-items-center justify-content-between p-3 bg-white shadow-sm rounded border mb-4">
                <h4 className="mb-0 fw-bold">
                    <i className="bi bi-people-fill me-2"></i>
                    Issue wise Time in JIRA Issues
                </h4>
            </div>
            {sprintLoading && (
                <div className="d-flex justify-content-center my-5">
                    <div className="p-4 bg-white rounded shadow-sm border text-center" style={{ minWidth: '300px' }}>
                        <FontAwesomeIcon icon={faSpinner} spin size="2x" className="text-primary mb-3" />
                        <h6 className="mb-0 text-muted">Loading Active Sprint...</h6>
                    </div>
                </div>
            )}
            {sprintData && (
                <Box className="mb-4">
                    <Typography variant="h5" color="green">Active Sprint: {sprintData.sprint_name}</Typography>
                    <Typography variant="body1">
                        <strong>Started At:</strong> {sprintData.sprint_start} | <strong>Ends At:</strong> {sprintData.sprint_end}
                    </Typography>

                    <Box mt={2} sx={{ maxHeight: 350, overflow: 'auto' }}>
                        <MaterialReactTable
                            columns={columns}
                            data={sprintData?.issues ?? []}
                            isLoading={!sprintData}
                            enableDensityToggle={false}
                            enableGlobalFilterToggle={false}
                            enablePagination={false}
                            initialState={{
                                showGlobalFilter: true,
                                density: 'compact',
                            }}
                            options={{
                                filtering: true,
                                search: true,
                                sorting: true,
                            }}
                            searchText={searchText}
                            onSearchChange={(text) => setSearchText(text)}
                            onOrderChange={(orderBy, orderDirection) => {
                                setSorting([{ column: columns[orderBy].field, direction: orderDirection }]);
                            }}
                            onFilterChange={(filters) => {
                                setTableFilters(filters);
                            }}
                        />
                    </Box>
                </Box>
            )}

            <div className="mb-3 d-flex gap-2">
                <input
                    type="text"
                    value={query}
                    onChange={e => { setQuery(e.target.value); }}
                    placeholder="Search for any Issue by its key, For Example: SHP-100"
                    className="form-control"
                />
                <button className="btn btn-primary" onClick={() => handleSearch()}>
                    Search
                </button>
                <button className="btn btn-danger" onClick={() => resetSearch()}>
                    Reset
                </button>
            </div>
            {loading && (
                <div className="d-flex justify-content-center my-5">
                    <div className="p-4 bg-white rounded shadow-sm border text-center" style={{ minWidth: '300px' }}>
                        <FontAwesomeIcon icon={faSpinner} spin size="2x" className="text-primary mb-3" />
                        <h6 className="mb-0 text-muted">Processing your request...</h6>
                    </div>
                </div>
            )}
            {Object.keys(results).length === 0 && !loading ? (
            noRecordFound ? (
                <p className="text-center text-muted">No status change history found for the Ticket.</p>
            ) : (
                <p className="text-center text-muted">No results found. Please search for a valid Ticket.</p>
            )
            ) : (
                <>
                    {issueDesc && Object.keys(issueDesc).length > 0 && (
                        <>
                            <div className="row">
                                <div className="col-sm-12">
                                    <div className="border rounded p-3 bg-light">
                                        <h5 className="mt-1 mb-2">
                                            <strong>Key:</strong>{' '}
                                            <a
                                                style={{ textDecoration: 'none' }}
                                                href={issueDesc.issueUrl}
                                                rel="noreferrer"
                                                target="_blank"
                                            >
                                                {issueDesc.issueKey}{' '}
                                                <FontAwesomeIcon icon={faUpRightFromSquare} className="ms-1" />
                                            </a>
                                        </h5>
                                        <hr className="my-2" />
                                        <h6 className="mb-2">
                                            <strong>Issue Type:</strong> {issueDesc.issueType}
                                        </h6>
                                        <h6 className="mb-2">
                                            <strong>Summary:</strong> {issueDesc.summary}
                                        </h6>
                                        <h6 className="mb-2">
                                            <strong>Status:</strong> {issueDesc.status}
                                        </h6>
                                        <h6 className="mb-2">
                                            <strong>Assignee:</strong> {issueDesc.assignee}
                                        </h6>
                                        <h6 className="mb-0">
                                            <strong>Reporter:</strong> {issueDesc.reporter}
                                        </h6>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                    <ul className="list-group">
                        {Object.entries(results).map(([id, item]) => (
                            <li key={id} className="list-group-item border rounded-3 shadow-sm mb-3 p-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <div>
                                        <h5 className="mb-1 text-primary">{item.status}</h5>
                                        <small className="text-muted">
                                            <strong>Total Duration:</strong> {item.durationDays}d {item.durationHours}h {item.durationMinutes}m
                                        </small>
                                    </div>
                                    <button
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => toggleExpand(id)}
                                    >
                                        History <FontAwesomeIcon icon={expandedItems[id] ? faCaretUp : faCaretDown} />
                                    </button>
                                </div>

                                {expandedItems[id] && (
                                    <div className="border-top pt-3">
                                        {item.history && item.history.length > 0 ? (
                                            <div className="accordion-body">
                                                {item.history.map((h, idx) => (
                                                    <div key={idx} className="mb-4 ps-2 border-start border-3 border-info">
                                                        <p className="mb-1">
                                                            <strong>From Status:</strong> {h.fromString}
                                                        </p>
                                                        <p className="mb-1">
                                                            <strong>Entered:</strong> {h.entered}
                                                        </p>
                                                        <p className="mb-1">
                                                            <strong>Exited:</strong> {h.exited}
                                                        </p>
                                                        <p className="mb-1">
                                                            <strong>Duration:</strong> {h.durationDays}d {h.durationHours}h {h.durationMinutes}m
                                                        </p>
                                                        {/* <p className="mb-2">
                                                            <strong>Action By:</strong> {h.author}
                                                        </p> */}

                                                        {/* Assignee Duration Block */}
                                                        <div className="p-2">
                                                            <p className="mb-1">
                                                                <strong>Assignee Timeline:</strong>
                                                            </p>

                                                            {Array.isArray(h.assignees) ? (
                                                                h.assignees.filter(a => a && typeof a === 'object').length > 0 ? (
                                                                    <ul className="list-unstyled mb-0">
                                                                        {h.assignees
                                                                            .filter(a => a && typeof a === 'object')
                                                                            .map((a, i) => (
                                                                                <li key={i} className="mb-1 bg-body-secondary">
                                                                                    <div>
                                                                                        {a.assignee && a.assignee.toLowerCase() === 'unassigned' ? (
                                                                                            <em>{a.assignee}</em>
                                                                                        ) : (
                                                                                            <span>{a.assignee}</span>
                                                                                        )} — {a.durationDays}d {a.durationHours}h {a.durationMinutes}m
                                                                                    </div>
                                                                                    <small className="text-muted">
                                                                                        <b>From:</b> {a.entered} &nbsp;|&nbsp; <b>To:</b> {a.exited}
                                                                                    </small>
                                                                                </li>
                                                                            ))}
                                                                    </ul>
                                                                ) : (
                                                                    <em>No assignment changes recorded</em>
                                                                )
                                                            ) : (
                                                                <em>No assignment changes recorded</em>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-muted"><em>No history available</em></p>
                                        )}
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>

                </>
            )}
        </div>
    );
}

export default TrackerPage;